import type { EmailDocumentKey } from '../api/emailTemplates/documentKeys';

/**
 * The documents that go to the customer, so the dialog may open addressed to
 * them. Vouchers, refunds, remittances and PRQs go to suppliers, an agent
 * invoice to the agent, and the passenger / rooming / hotel lists and tour
 * reports to hotels and guides — pre-filling a customer there would send one
 * customer everyone's details.
 */
export const CUSTOMER_FACING_DOCUMENT_KEYS: EmailDocumentKey[] = [
    'INVOICE',
    'TAX_INVOICE',
    'RECEIPT',
    'TRANSFER_RECEIPT',
    'CREDIT_DEBIT',
    'DEBIT_CREDIT',
    'STATEMENT',
    'REPORT_TRAVEL_FILE_DETAILS',
    'REPORT_ITINERARY'
];

/**
 * Who a customer document email is addressed to when the dialog opens: the
 * lead passenger's email. The sender can still remove it or add more. Nothing
 * when the lead has no email — deliberately no fallback to `travelfile.email`,
 * which on imported files holds the Pegasus handler's mailbox, not the
 * customer's (and is empty on registration files). Nothing for a document that is not
 * customer-facing, or for a file that is not a client file (a group file's
 * passengers belong to many customers).
 *
 * PG passenger rows carry the address flat (`email`, stamped by
 * attachPassengerContacts); `emails[0].email` is the legacy nested shape.
 * The lead is the row marked `isLead`, else the first passenger — the same
 * choice buildMergeContext makes for `<First Customer Name>`.
 */
export function getDefaultRecipientEmails(travelFile: any, documentKey?: EmailDocumentKey | null): string[] {
    if (!documentKey || !CUSTOMER_FACING_DOCUMENT_KEYS.includes(documentKey)) return [];
    if (travelFile?.type !== 'client') return [];
    const passengers: any[] = Array.isArray(travelFile?.passengers) ? travelFile.passengers : [];
    const lead = passengers.find((p) => p?.isLead) ?? passengers[0];
    const email = [lead?.email, lead?.emails?.[0]?.email]
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .find(Boolean);
    return email ? [email.toLowerCase()] : [];
}
